import { useS4LContext } from '@s4l/react-context'
import { Money } from '@s4l/stayforlong/kernel/Money'
import { Currency, type Currencies } from '@s4l/stayforlong/kernel/Currency'
import { Market } from '@s4l/stayforlong/shared/domain/Market'
import { useUseCase } from '@s4l/utils/hooks/useUseCase'
import { getTaxTranslationKey, isKnownTaxType } from './utils'

export const usePriceDetailsHelpers = () => {
  const { i18n } = useS4LContext()
  const {
    data: market = Market.default(),
    loading: _loadingMarket,
    error: _errorMarket,
  } = useUseCase('GetCurrentMarketSharedUseCase')

  const getTaxDisplayName = (taxName: string): string => {
    if (isKnownTaxType(taxName)) {
      const translationKey = getTaxTranslationKey(taxName)
      return i18n.formatMessage({ id: translationKey })
    }

    // Fallback for unknown tax types
    return taxName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }

  const formatCurrency = (amount?: number, currency?: string) => {
    if (amount == null || !currency) return null

    return Money.create({
      amount,
      currency: Currency.create({ value: currency as Currencies }),
    }).toFormattedString(i18n.locale, market)
  }

  const formatMessageCapitalized = (
    message: { id: string },
    values?: Record<string, string | number | undefined>
  ) => {
    const text = i18n.formatMessage(message, values)
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase()
  }

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString)
    return i18n.formatDate(date, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  return {
    getTaxDisplayName,
    formatCurrency,
    formatMessage: i18n.formatMessage,
    formatMessageCapitalized,
    formatDate,
  }
}
